import { Handle, NodeToolbar, Position } from "@xyflow/react";
import { AnimateIcon } from "@quake/shared-ui/components/animate-ui/icons/icon";
import RepeatIcon from "lucide-react/dist/esm/icons/repeat";
import PlusIcon from "lucide-react/dist/esm/icons/plus";
import TrashIcon from "lucide-react/dist/esm/icons/trash";
import PencilIcon from "lucide-react/dist/esm/icons/pencil";
import EyeIcon from "lucide-react/dist/esm/icons/eye";
import { Button } from "@quake/shared-ui/components/ui/button";
import { cn } from "@quake/shared-ui/lib/utils";
import { useWorkflowBuilderStore } from "../store/useWorkflowBuilderStore";
import { NodeData } from "../types";
import { useState } from "react";
import CheckCircle2Icon from "lucide-react/dist/esm/icons/check-circle-2";
import XCircleIcon from "lucide-react/dist/esm/icons/x-circle";

export type IteratorNodeProps = {
  id: string;
  data: NodeData & {
    iterator?: {
      collection?: string;
      filter?: string | null;
      maxIterations?: number | null;
      stopOnFailure?: boolean | null;
    };
    childNodes?: string[];
  };
  onAddAction?: ({ branch }: { branch?: "truthy" | "falsy" }) => void;
  onDelete?: () => void;
  onEdit?: () => void;
  readOnly?: boolean;
  onAddChildAction?: () => void;
  allowActionPreview?: boolean;
};

export function IteratorNode({
  id,
  data,
  onAddAction,
  onDelete,
  onEdit,
  readOnly = false,
  onAddChildAction,
  allowActionPreview = true,
}: IteratorNodeProps) {
  const direction = useWorkflowBuilderStore((state) => state.meta?.direction);
  const [isDragOver, setIsDragOver] = useState(false);

  const settings: string[] = [];
  const filterPreview = data.action?.parameters?.[0]?.input?.filter?.toString().trim();
  if (filterPreview) {
    settings.push(`Filter: ${filterPreview}`);
  }
  if (data.iterator?.maxIterations != null) {
    settings.push(`Max: ${data.iterator.maxIterations}`);
  }
  if (data.iterator?.stopOnFailure === false) {
    settings.push("Continue on failure");
  }

  const childCount = data.childNodes?.length || 0;

  return (
    <div
      data-node-id={id}
      className="relative"
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragOver(true);
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={() => setIsDragOver(false)}
    >
      {allowActionPreview ? (
        <NodeToolbar isVisible position={Position.Top} className="flex gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 bg-white hover:bg-gray-100 shadow-sm shadow-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700 dark:shadow-slate-200 dark:shadow-none dark:border dark:border-slate-500 dark:text-slate-200"
            onClick={onEdit}
          >
            {readOnly ? (
              <EyeIcon className="h-4 w-4 text-gray-600 dark:text-slate-200" />
            ) : (
              <PencilIcon className="h-4 w-4 text-gray-600 dark:text-slate-200" />
            )}
          </Button>
          {!readOnly && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 bg-white hover:bg-gray-100 shadow-sm shadow-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700 dark:shadow-slate-200 dark:shadow-none dark:border dark:border-slate-500 dark:text-slate-200"
              onClick={onDelete}
            >
              <TrashIcon className="h-4 w-4 text-red-600" />
            </Button>
          )}
        </NodeToolbar>
      ) : null}
      {/* Container Box */}
      <div
        className={cn(
          "rounded-lg border-2 border-dashed bg-linear-to-br from-sky-50/50 to-cyan-50/50",
          "dark:from-slate-800/50 dark:to-slate-900/50",
          "border-sky-400 dark:border-cyan-500",
          "min-w-100 min-h-75",
          "transition-all duration-200",
          isDragOver && "border-sky-600 dark:border-cyan-400 bg-sky-100/70 dark:bg-slate-700/70",
          !readOnly && childCount === 0 && "cursor-pointer hover:border-sky-500 dark:hover:border-cyan-400",
        )}
        style={{
          padding: "50px",
        }}
        onClick={(e) => {
          // Only handle click if empty and not in readOnly mode
          if (!readOnly && childCount === 0 && onAddChildAction) {
            e.stopPropagation();
            onAddChildAction();
          }
        }}
      >
        {/* Header */}
        <div className="absolute top-2 left-2 right-2 flex items-center justify-between gap-2 pointer-events-none">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-linear-to-r from-sky-500 to-cyan-500 text-white shadow-md">
            <AnimateIcon animateOnHover>
              <RepeatIcon className="h-4 w-4" />
            </AnimateIcon>
            <span className="font-medium text-sm">{data.label || "Iterator"}</span>
            {childCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-white/20 rounded text-xs">
                {childCount} {childCount === 1 ? "node" : "nodes"}
              </span>
            )}
          </div>
          {settings.length > 0 && (
            <div className="text-xs text-gray-600 dark:text-gray-400 bg-white/80 dark:bg-slate-800/80 px-2 py-1 rounded shadow-sm">
              {settings.join(" • ")}
            </div>
          )}
        </div>

        {/* Drop Zone Hint */}
        {childCount === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center text-gray-400 dark:text-gray-500">
              <RepeatIcon className="h-12 w-12 mx-auto mb-2 opacity-30" />
              <p className="text-sm">Drag nodes here or use + button</p>
            </div>
          </div>
        )}

        {/* Handles */}
        <Handle
          id="target"
          type="target"
          position={direction === "top-to-bottom" ? Position.Top : Position.Left}
          className="bg-sky-500! w-3! h-3!"
          style={{
            top: direction === "top-to-bottom" ? -6 : "50%",
            left: direction === "top-to-bottom" ? "50%" : -6,
          }}
        />
        <Handle
          id="source"
          type="source"
          position={direction === "top-to-bottom" ? Position.Bottom : Position.Right}
          className="bg-cyan-500! w-3! h-3!"
          style={{
            bottom: direction === "top-to-bottom" ? -6 : "auto",
            top: direction === "top-to-bottom" ? "auto" : "50%",
            right: direction === "top-to-bottom" ? "auto" : -6,
            left: direction === "top-to-bottom" ? "50%" : "auto",
          }}
        />
        {data.output && (
          <div className="absolute -top-2 -right-2 bg-green-500 rounded-full p-1 cursor-pointer" onClick={onEdit}>
            <CheckCircle2Icon className="w-4 h-4 text-white" />
          </div>
        )}
        {data.output === false && (
          <div className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1 cursor-pointer" onClick={onEdit}>
            <XCircleIcon className="w-4 h-4 text-white" />
          </div>
        )}
      </div>

      {/* Note */}
      {data.note && <div className="automation-node-note">{data.note}</div>}

      {/* Add Action Button */}
      {!readOnly && (
        <NodeToolbar isVisible position={direction === "top-to-bottom" ? Position.Bottom : Position.Right}>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 bg-white hover:bg-gray-100 shadow-sm shadow-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700 dark:shadow-slate-200 dark:shadow-none dark:border dark:border-slate-500 dark:text-slate-200"
            onClick={() => onAddAction?.({})}
          >
            <PlusIcon className="h-4 w-4" />
          </Button>
        </NodeToolbar>
      )}
    </div>
  );
}
